import axios from "axios";
import router from "@/routes";
const RX_HARMONY_IDS = ["840b1480-1439-4aa4-9e4b-0fdba48add55", "4c205bd3-d707-4d83-8f25-ba493e6eaa02", "4c8fefbb-454b-40ac-b98f-31acb6a1fa5c"];

function cobrandOf(organization) {
  if (!organization) return "";
  if (organization.cobrandId) return organization.cobrandId;
  if (RX_HARMONY_IDS.indexOf(organization.id) !== -1) return "rx-harmony";
  if (organization.description === "Rx Harmony") return "rx-harmony";
  return "";
}

export default {
  strict: true,
  namespaced: true,
  state: {
    organization: null,
    organizations: [],
    portalTheme: null,
    cobrandRefreshKey: 0,
  },
  getters: {
    organization: (state) => state.organization,
    organizations: (state) => state.organizations,
    hasMultipleOrganizations: (state) => state.organizations && state.organizations.length > 1,
    isOrganizationPredictile: (state) => state.organization && state.organization.type === "predictile",
    isOrganizationBusiness: (state) => state.organization && state.organization.type === "business",
    isOrganizationUser: (state) => state.organization && state.organization.type === "user",
    // RxH dynamic role/permission matrix is enabled per-business (off everywhere by default).
    isRoleMatrixEnabled: (state) => !!state.organization && state.organization.useRoleMatrix === true,
    cobrandId(state) {
      const fromOrganization = cobrandOf(state.organization);
      if (fromOrganization) return fromOrganization;

      state.cobrandRefreshKey; // use to trigger refresh

      if (state.organization && state.organization.type === "business") return "";

      return localStorage.cobrand || "";
    },

    portalVersion(state, getters) {
      if (getters.cobrandId !== "rx-harmony") return "v1";
      if (getters.isOrganizationPredictile) return "v1";

      return "v2";
    },
    isPortalV2: (state, getters) => getters.portalVersion === "v2",
    portalTheme: (state) => state.portalTheme,
  },
  mutations: {
    setPortalTheme(state, payload) {
      state.portalTheme = payload || {};
      try {
        const org = state.organization;
        if (org && payload) localStorage.setItem("portalTheme:" + org.id, JSON.stringify(payload));
      } catch {
        // Private browsing, or a quota that is full.
      }
    },
    setOrganization(state, payload) {
      state.organization = payload;
      try {
        if (payload && payload.type !== "predictile") {
          const cobrand = cobrandOf(payload);
          if (cobrand) localStorage.setItem("cobrand", cobrand);
          else if (payload.type === "business") localStorage.removeItem("cobrand");
        } else if (payload && payload.type === "predictile") {
          state.portalTheme = null;
          localStorage.removeItem("cobrand");
          localStorage.removeItem("portalVersion");
          Object.keys(localStorage)
            .filter((key) => key.indexOf("portalTheme:") === 0)
            .forEach((key) => localStorage.removeItem(key));
        }
      } catch {
        // Private browsing, or a quota that is full.
      }
    },
    setOrganizations(state, payload) {
      state.organizations = payload;
    },
    activateOrganization(state) {
      state.organization.isActive = true;
    },
    setCobrand(state, cobrand) {
      localStorage.setItem("cobrand", cobrand);
      state.cobrandRefreshKey++;
    },
  },
  actions: {
    init: async (context) => {
      // is authenticated
      if (!context.rootGetters["cognito/isAuthenticated"]) {
        context.commit("setOrganization", null);
        sessionStorage.removeItem("organization/id");
        return;
      }

      // organizations
      try {
        const organizationsResponse = await axios.get("/organizations");
        context.commit("setOrganizations", organizationsResponse.data);
        if (!organizationsResponse.data || organizationsResponse.data.length === 0) {
          router.push("/login-error");
          return;
        }
      } catch (error) {
        if (error.response && error.response.data) {
          const errors = error.response.data;
          const errorList = Array.isArray(errors) ? errors : errors.errors;
          if (Array.isArray(errorList) && errorList.some((e) => e.message === "ACCOUNT_DELETED")) {
            router.push("/login-error");
            return;
          }
        }

        const hasStartedPinMode = sessionStorage.getItem("hasStartedPinMode") === "true";
        if (hasStartedPinMode) {
          router.push("/pin-mode");
        } else {
          router.push("/signout");
        }
        return;
      }

      // organization
      var organizationId = sessionStorage.getItem("organization/id");
      if (organizationId) {
        await axios
          .get(`/organizations/${organizationId}`)
          .then((res) => {
            context.commit("setOrganization", res.data);
            sessionStorage.setItem("organization/id", res.data.id);
          })
          .catch(() => {
            var organization = context.state.organizations.find((o) => o.type === "user");
            context.commit("setOrganization", organization);
            sessionStorage.setItem("organization/id", organization.id);
          });
      } else {
        var organization = {};

        if (context.state.organizations.find((o) => o.type === "predictile")) {
          organization = context.state.organizations.find((o) => o.type === "predictile");
          context.commit("setOrganization", organization);
          sessionStorage.setItem("organization/id", organization.id);
          return;
        } else if (context.state.organizations.find((o) => o.type === "business")) {
          organization = context.state.organizations.find((o) => o.type === "business");
          context.commit("setOrganization", organization);
          sessionStorage.setItem("organization/id", organization.id);
          return;
        } else {
          organization = context.state.organizations.find((o) => o.type === "user");
          context.commit("setOrganization", organization);
          if (organization) {
            sessionStorage.setItem("organization/id", organization.id);
          }
          return;
        }
      }
    },
    loadPortalTheme: async (context) => {
      const org = context.state.organization;
      if (!org || org.type !== "business" || context.state.portalTheme) return;

      try {
        const cached = localStorage.getItem("portalTheme:" + org.id);
        if (cached) context.commit("setPortalTheme", JSON.parse(cached));
      } catch {
        // No cache, or unreadable - the design defaults stand until the fetch.
      }

      try {
        const res = await axios.get(`/businesses/${org.id}/themes`);
        const themes = Array.isArray(res.data) ? res.data : [];
        context.commit("setPortalTheme", themes.find((t) => t.isSelected) || themes[0] || null);
      } catch {
        context.commit("setPortalTheme", null);
      }
    },
    changeOrganization: async (context, organizationId) => {
      if (!organizationId) return;
      await axios
        .get(`/organizations/${organizationId}`)
        .then((res) => {
          context.commit("setOrganization", res.data);
          sessionStorage.setItem("organization/id", res.data.id);
        })
        .catch(() => {
          var organization = context.state.organizations.find((o) => o.type === "user");
          context.commit("setOrganization", organization);
          sessionStorage.setItem("organization/id", organization.id);
        });
    },
    clearOrganization: async (context) => {
      context.commit("setOrganization", null);
      context.commit("setOrganizations", null);
    },
    activateOrganization: async (context) => {
      context.commit("activateOrganization");
    },
  },
};
